import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import {
  Alert,
  Box,
  Button,
  type DialogProps,
  Typography,
} from '@mui/material';
import { Trash2 } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import {
  DropZoneButton,
  FullScreenDropZone,
} from '@modules/_shared/components/DropZone';
import type { ImageGalleryItem } from '@modules/_shared/components/image-gallery/NewImageGallery';
import { useGalleryUploads } from '@modules/_shared/components/image-gallery/useGalleryUploads';
import {
  GalleryPhotoGrid,
  GalleryPhotoGridSkeleton,
} from '@modules/_shared/components/image-upload/components/GalleryPhotoGrid';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useLocale } from '@shared/hooks/locale';

const GET_USER_IMAGES_FOR_GALLERY = gql(/* GraphQL */ `
  query GetUserImagesForGallery($userId: uuid!) {
    user_images(
      where: { user_id: { _eq: $userId } }
      order_by: { image: { created_at: desc } }
    ) {
      id
      is_primary
      image {
        id
        url
      }
    }
  }
`);

const GET_PROPERTY_IMAGES_FOR_GALLERY = gql(/* GraphQL */ `
  query GetPropertyImagesForGallery($propertyId: uuid!) {
    property_images(
      where: { property_id: { _eq: $propertyId } }
      order_by: { image: { created_at: desc } }
    ) {
      id
      image {
        id
        url
      }
    }
  }
`);

const GET_TEAM_IMAGES_FOR_GALLERY = gql(/* GraphQL */ `
  query GetTeamImagesForGallery($teamId: uuid!) {
    teams_by_pk(id: $teamId) {
      id
      logo_image_id
      cover_photo_image_id
      team_images(order_by: { image: { created_at: desc } }) {
        id
        order_nr
        image {
          id
          url
        }
      }
    }
  }
`);

const DELETE_USER_IMAGE_FROM_GALLERY = gql(/* GraphQL */ `
  mutation DeleteUserImageFromGallery($id: uuid!) {
    delete_user_images_by_pk(id: $id) {
      id
    }
  }
`);

const DELETE_TEAM_IMAGE_FROM_GALLERY = gql(/* GraphQL */ `
  mutation DeleteTeamImageFromGallery($id: uuid!) {
    delete_team_images_by_pk(id: $id) {
      id
    }
  }
`);

type ImageGalleryDialogProps = {
  open: boolean;
  onClose: () => void;
  entityId: string;
  entityType: 'user' | 'property' | 'team';
  onSelectImage: (image: ImageGalleryItem) => void;
  onFilesUploaded: (images: { id: string; url: string }[]) => Promise<void>;
  maxWidth?: DialogProps['maxWidth'];
};

const ENTITY_CONFIG = {
  user: {
    query: GET_USER_IMAGES_FOR_GALLERY,
    dataKey: 'user_images' as const,
    variableKey: 'userId' as const,
    canDelete: true,
  },
  property: {
    query: GET_PROPERTY_IMAGES_FOR_GALLERY,
    dataKey: 'property_images' as const,
    variableKey: 'propertyId' as const,
    canDelete: false,
  },
  team: {
    query: GET_TEAM_IMAGES_FOR_GALLERY,
    dataKey: 'team_images' as const,
    variableKey: 'teamId' as const,
    canDelete: true,
  },
} as const;

export const ImageGalleryDialog = memo(
  ({
    open,
    onClose,
    entityId,
    entityType,
    onSelectImage,
    onFilesUploaded,
    maxWidth,
  }: ImageGalleryDialogProps) => {
    const { t } = useLocale();
    const { me } = useAppData();

    const userQuery = useQuery(GET_USER_IMAGES_FOR_GALLERY, {
      variables: { userId: entityId },
      skip: !open || entityType !== 'user',
    });

    const propertyQuery = useQuery(GET_PROPERTY_IMAGES_FOR_GALLERY, {
      variables: { propertyId: entityId },
      skip: !open || entityType !== 'property',
    });

    const teamQuery = useQuery(GET_TEAM_IMAGES_FOR_GALLERY, {
      variables: { teamId: entityId },
      skip: !open || entityType !== 'team',
    });

    const queries = {
      user: userQuery,
      property: propertyQuery,
      team: teamQuery,
    };

    const { data, loading, error, refetch } = queries[entityType];
    const config = ENTITY_CONFIG[entityType];
    const canDeleteImages =
      config.canDelete && (entityType !== 'team' || me?.is_admin === true);

    const [
      deleteUserImage,
      {
        loading: deletingUserImage,
        error: deleteUserImageError,
        reset: resetDeleteUserImage,
      },
    ] = useMutation(DELETE_USER_IMAGE_FROM_GALLERY, {
      update: (cache, { data }) => {
        if (!data?.delete_user_images_by_pk) {
          return;
        }

        cache.evict({
          id: cache.identify({
            __typename: 'user_images',
            id: data.delete_user_images_by_pk.id,
          }),
        });
      },
    });

    const [
      deleteTeamImage,
      {
        loading: deletingTeamImage,
        error: deleteTeamImageError,
        reset: resetDeleteTeamImage,
      },
    ] = useMutation(DELETE_TEAM_IMAGE_FROM_GALLERY, {
      update: (cache, { data }) => {
        if (!data?.delete_team_images_by_pk) {
          return;
        }

        cache.evict({
          id: cache.identify({
            __typename: 'team_images',
            id: data.delete_team_images_by_pk.id,
          }),
        });
      },
    });

    // Refetch when dialog opens to ensure fresh data
    useEffect(() => {
      if (open) {
        refetch();
      }
    }, [open, refetch]);

    const images = useMemo(() => {
      if (!data) {
        return [];
      }

      let items: any[] = [];
      let logoImageId: string | null = null;
      let coverPhotoImageId: string | null = null;

      if (entityType === 'user' && 'user_images' in data) {
        items = data.user_images;
      } else if (entityType === 'property' && 'property_images' in data) {
        items = data.property_images;
      } else if (
        entityType === 'team' &&
        'teams_by_pk' in data &&
        data.teams_by_pk
      ) {
        items = data.teams_by_pk.team_images ?? [];
        // Get the team's current logo and cover photo IDs
        logoImageId = data.teams_by_pk.logo_image_id ?? null;
        coverPhotoImageId = data.teams_by_pk.cover_photo_image_id ?? null;
      }

      return items.map((item, index) => {
        const imageId = item.image.id;

        // Determine if the image can be deleted
        let canDelete = true;

        if (entityType === 'user' && 'is_primary' in item) {
          // For user images, prevent deletion of primary image
          canDelete = !item.is_primary;
        } else if (entityType === 'team') {
          // For team images, prevent deletion if it's the current logo or banner
          canDelete = imageId !== logoImageId && imageId !== coverPhotoImageId;
        }

        return {
          id: item.id,
          // We do not need order_nr in this use case as there is no re-order logic
          // It can also be null, so we use index as fallback
          order_nr: item.order_nr ?? index,
          image: {
            id: imageId,
            url: item.image.url ?? '',
          },
          canDelete,
          is_primary: item.is_primary === true,
        };
      });
    }, [data, entityType]);

    const handleFilesUploaded = useCallback(
      async (uploadedFiles: { id: string; url: string }[]) => {
        await onFilesUploaded(uploadedFiles);
        await refetch();
      },
      [onFilesUploaded, refetch],
    );

    const { uploadingImages, handleFilesDrop } = useGalleryUploads({
      onFilesUploaded: handleFilesUploaded,
    });
    const isUploading = uploadingImages.some(
      upload => upload.state === 'uploading',
    );

    const [imageToDelete, setImageToDelete] = useState<string | null>(null);

    const handleDeleteImage = useCallback(async () => {
      if (imageToDelete == null) {
        return;
      }
      try {
        if (entityType === 'user') {
          await deleteUserImage({ variables: { id: imageToDelete } });
        } else if (entityType === 'team') {
          await deleteTeamImage({ variables: { id: imageToDelete } });
        }
      } catch {
        return;
      } finally {
        setImageToDelete(null);
      }
    }, [imageToDelete, entityType, deleteUserImage, deleteTeamImage]);

    const renderContent = () => {
      if (loading && !data) {
        return <GalleryPhotoGridSkeleton />;
      }
      if (error && !data) {
        return (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                {t('Try again')}
              </Button>
            }
          >
            {t('Could not load the photos')}
          </Alert>
        );
      }
      return (
        <GalleryPhotoGrid
          images={images}
          uploadingImages={uploadingImages}
          onSelectImage={onSelectImage}
          onDeleteImage={canDeleteImages ? setImageToDelete : undefined}
        />
      );
    };

    return (
      <SlideDialog
        open={open}
        onClose={onClose}
        title={t('Choose from gallery')}
        maxWidth={maxWidth ?? 'md'}
        fullWidth
      >
        <FullScreenDropZone onDrop={handleFilesDrop} />
        <Box sx={{ p: 3, pt: 2 }}>
          <Box sx={{ mx: -2, mb: 2 }}>
            <DropZoneButton
              onDrop={handleFilesDrop}
              accept="image/*"
              multiple
              isLoading={isUploading}
            />
          </Box>
          {renderContent()}
        </Box>
        <SlideDialog
          open={imageToDelete != null}
          onClose={() => setImageToDelete(null)}
          title={t('Delete image')}
          maxWidth="xs"
          cancelText={t('Cancel')}
          saveText={t('Delete')}
          saveColor="error"
          saveIcon={<Trash2 size={16} />}
          loading={deletingUserImage || deletingTeamImage}
          onSave={handleDeleteImage}
        >
          <Typography sx={{ p: 2 }}>
            {t('Are you sure you want to delete this image?')}
          </Typography>
        </SlideDialog>
        <MutationErrorSnackbar
          error={deleteUserImageError ?? deleteTeamImageError ?? null}
          onClose={() => {
            resetDeleteUserImage();
            resetDeleteTeamImage();
          }}
        />
      </SlideDialog>
    );
  },
);
